import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import EventFormDialog from "@/components/EventFormDialog";
import { useEventsStore } from "@/data/eventsStore";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Trash2, Edit, AlertCircle, Clock, CheckCircle, XCircle, History, Save } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";

const OrgEventsPage = () => {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [events, setEvents] = useState<any[]>([]);
  const [historyEvents, setHistoryEvents] = useState<any[]>([]);
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null);

  useEffect(() => {
    loadMyEvents();
  }, [user]);

  const loadMyEvents = async () => {
    if (!user) return;
    
    try {
      const store = useEventsStore.getState();
      
      // Load public events for the current user
      await store.fetchEvents();
      const allEvents = store.events;
      
      // Filter events created by the current user
      const userEvents = allEvents.filter(e => e.userId === user.id || e.partyAcronym === (user.partyAcronym || user.name));

      // Separate into active and history (if we ever support history from API)
      // For now, API only returns current state, not history.
      // So we'll just handle current events.
      
      userEvents.sort((a, b) => new Date(b.submittedAt || b.date).getTime() - new Date(a.submittedAt || a.date).getTime());
      
      setEvents(userEvents);
      setHistoryEvents([]); // We'll keep this empty for now until history API is implemented
    } catch (err) {
      console.error("Error loading events", err);
    }
  };

  const handleDelete = async () => {
    if (!deleteEventId) return;

    try {
      const store = useEventsStore.getState();
      
      // Mettre à jour le statut en 'annule'
      const success = await store.updateEventStatus(deleteEventId, 'annule');
      
      if (success) {
        toast.success("Événement annulé avec succès");
        setDeleteEventId(null);
        loadMyEvents();
      } else {
        toast.error("Erreur lors de l'annulation de l'événement.");
      }
    } catch (err) {
       console.error("Delete error", err);
       toast.error("Une erreur est survenue.");
    }
  };

  const handleEdit = (event: any) => {
    localStorage.setItem("kaynin_edit_event", JSON.stringify(event));
    navigate("/proposer-evenement");
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approuve":
      case "publie":
        return <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white flex gap-1"><CheckCircle className="w-3 h-3" /> Publié</Badge>;
      case "rejete":
        return <Badge className="bg-red-500 hover:bg-red-600 text-white flex gap-1"><XCircle className="w-3 h-3" /> Rejeté</Badge>;
      case "annule":
        return <Badge className="bg-slate-500 hover:bg-slate-600 text-white flex gap-1"><Trash2 className="w-3 h-3" /> Annulé</Badge>;
      case "draft":
        return <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 flex gap-1"><Save className="w-3 h-3" /> Brouillon</Badge>;
      case "archive":
        return <Badge variant="secondary" className="flex gap-1"><History className="w-3 h-3" /> Archive (Avant Modif)</Badge>;
      default:
        return <Badge className="bg-amber-500 hover:bg-amber-600 text-white flex gap-1"><Clock className="w-3 h-3" /> En attente</Badge>;
    }
  };

  const renderEventList = (list: any[], showActions: boolean = true) => {
    if (list.length === 0) {
      return (
        <div className="col-span-full p-8 text-center bg-card rounded-xl border border-border mt-4">
          <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-bold text-foreground mb-2">Aucun événement</h3>
          <p className="text-muted-foreground">Aucun événement dans cette catégorie.</p>
        </div>
      );
    }

    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
        {list.map((event) => (
          <Card key={event.id + (event.modifiedAt || '')} className="overflow-hidden flex flex-col opacity-100 transition-opacity">
            {event.imageUrl && (
              <div className="h-40 w-full overflow-hidden bg-white">
                <img src={event.imageUrl} alt={event.title} className="w-full h-full object-contain" />
              </div>
            )}
            <CardHeader className="p-4 pb-2">
              <div className="flex justify-between items-start mb-2 gap-2">
                <CardTitle className="text-lg font-display leading-tight line-clamp-2">{event.title}</CardTitle>
              </div>
              {getStatusBadge(event.status)}
            </CardHeader>
            <CardContent className="p-4 pt-0 flex-1 flex flex-col justify-between">
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-muted-foreground font-body">
                  <Calendar className="w-4 h-4 mr-2" />
                  {new Date(event.date).toLocaleDateString()}
                </div>
                <div className="flex items-center text-sm text-muted-foreground font-body">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span className="truncate">{event.location}</span>
                </div>
                {event.modifiedAt && (
                  <div className="flex items-center text-xs text-amber-600 font-bold bg-amber-50 p-1.5 rounded">
                    Modifié le {new Date(event.modifiedAt).toLocaleDateString()}
                  </div>
                )}
              </div>

              {showActions && event.status !== "annule" && (
                <div className="flex justify-between items-center pt-4 border-t border-border mt-auto gap-2">
                  <Button variant="outline" size="sm" className="flex-1 font-body text-xs h-8" onClick={() => handleEdit(event)}>
                    <Edit className="w-3 h-3 mr-1" /> Modifier
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="flex-1 font-body text-xs h-8" 
                    onClick={() => {
                      if (event.status === "rejete" && confirm("Supprimer définitivement cet événement rejeté ?")) {
                        useEventsStore.getState().deleteEvent(event.id).then(() => loadMyEvents());
                      } else if (event.status !== "rejete") {
                        setDeleteEventId(event.id);
                      }
                    }}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> {event.status === "rejete" ? "Supprimer" : "Annuler"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const activeEvents = events.filter(e => e.status !== "annule" && e.status !== "draft");
  const draftEvents = events.filter(e => e.status === "draft");
  const canceledEvents = events.filter(e => e.status === "annule");

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Mes Événements</h1>
          <p className="text-muted-foreground font-body">Gérez la traçabilité et le statut de vos événements.</p>
        </div>
        <Button onClick={() => { localStorage.removeItem("kaynin_edit_event"); navigate("/proposer-evenement"); }} className="gap-2 shrink-0 shadow-md">
          <Calendar className="w-4 h-4" /> Ajouter un événement
        </Button>
      </div>

      <Tabs defaultValue="actifs" className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-xl h-auto p-1 bg-muted/50 rounded-lg">
          <TabsTrigger value="actifs" className="rounded-md py-2 text-sm font-semibold">Actifs ({activeEvents.length})</TabsTrigger>
          <TabsTrigger value="brouillons" className="rounded-md py-2 text-sm font-semibold text-amber-700">Brouillons ({draftEvents.length})</TabsTrigger>
          <TabsTrigger value="annules" className="rounded-md py-2 text-sm font-semibold">Annulés ({canceledEvents.length})</TabsTrigger>
          <TabsTrigger value="historique" className="rounded-md py-2 text-sm font-semibold">Historique ({historyEvents.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="actifs">
          {renderEventList(activeEvents, true)}
        </TabsContent>
        
        <TabsContent value="brouillons">
          {renderEventList(draftEvents, true)}
        </TabsContent>
        
        <TabsContent value="annules">
          {renderEventList(canceledEvents, false)}
        </TabsContent>
        
        <TabsContent value="historique">
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
            Cet onglet conserve la version originale de vos événements avant chaque modification pour assurer une traçabilité totale.
          </div>
          {renderEventList(historyEvents, false)}
        </TabsContent>
      </Tabs>

      <Dialog open={!!deleteEventId} onOpenChange={(open) => !open && setDeleteEventId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" /> Confirmer l'annulation
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir annuler cet événement ? Il sera déplacé vers l'onglet "Annulés".
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteEventId(null)}>Retour</Button>
            <Button variant="destructive" onClick={handleDelete}>Oui, annuler l'événement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgEventsPage;
